import styled from 'styled-components';

import { BLOG_PREVIEW, MOBILE, TOAST_ANIM } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';

/**
 * The replicated whitelabels.com article anatomy (see the rendering contract in
 * `docs/blog-authoring-plan.md`): dark photo cover → rounded-top LIGHT body
 * chapter → light outro panels. Class names inside `PostBody` match the wl
 * renderer output (`post-figure`, `data-table`, `post-callout--kind`, …) so the
 * markdown renderer + these styles double as the casino front-end's contract.
 * The public site loads Satoshi; the preview inherits the studio font stack —
 * the one intentional divergence.
 */

/** The article "page" surface, dark like the public site regardless of theme. */
export const Root = styled.article`
  background: ${BLOG_PREVIEW.pageBg};
  border-radius: 20px;
  overflow: hidden;
`;

/* ── Cover ── */

export const Cover = styled.header`
  position: relative;
  min-height: clamp(20rem, 44vh, 30rem);
  display: flex;
  align-items: flex-end;
  background: ${BLOG_PREVIEW.coverFallback};
`;

export const CoverImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CoverVignette = styled.div`
  position: absolute;
  inset: 0;
  background: ${BLOG_PREVIEW.coverVignette};
`;

export const CoverInner = styled.div`
  position: relative;
  width: 100%;
  padding: 72px 40px 36px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${MOBILE} {
    padding: 56px 20px 28px;
  }
`;

export const Breadcrumbs = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${BLOG_PREVIEW.coverTextSoft};
`;

export const Crumb = styled.span`
  color: ${BLOG_PREVIEW.coverTextSoft};
`;

export const CrumbSep = styled.span`
  color: ${BLOG_PREVIEW.coverCredit};
`;

export const CrumbCurrent = styled.span`
  color: ${BLOG_PREVIEW.coverText};
`;

/** Category pill with the glowing accent dot (wl .hub-pill). */
export const HubPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 6px 14px;
  border-radius: 99px;
  background: ${BLOG_PREVIEW.pillBg};
  color: ${BLOG_PREVIEW.pillText};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const PillDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${BLOG_PREVIEW.accent};
`;

export const CoverTitle = styled.h1`
  margin: 0;
  color: ${BLOG_PREVIEW.coverText};
  font-size: clamp(1.9rem, 4.6vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.018em;
  line-height: 1.1;
  text-wrap: balance;
`;

export const Standfirst = styled.p`
  margin: 0;
  max-width: 720px;
  color: ${BLOG_PREVIEW.coverTextSoft};
  font-size: clamp(1.05rem, 1.9vw, 1.3rem);
  line-height: 1.5;
`;

export const Byline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  color: ${BLOG_PREVIEW.coverTextSoft};
  font-size: 13px;
`;

export const CoverCredit = styled.span`
  position: absolute;
  right: 16px;
  bottom: 10px;
  font-size: 11px;
  color: ${BLOG_PREVIEW.coverCredit};

  ${MOBILE} {
    display: none;
  }
`;

/* ── Light body chapter ── */

export const BodyArc = styled.div`
  position: relative;
  background: ${BLOG_PREVIEW.lightBg};
  border-radius: 32px 32px 0 0;
  padding: 40px 40px 48px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${MOBILE} {
    padding: 28px 20px 32px;
    border-radius: 24px 24px 0 0;
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 40px;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
`;

export const Main = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

/* At-a-glance answer box (speakable target on the casino site). */
export const AtAGlance = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 24px;
  border-radius: 14px;
  background: ${BLOG_PREVIEW.lightSurface};
  box-shadow: ${BLOG_PREVIEW.lightShadow};
`;

export const BoxLabel = styled(Text)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${BLOG_PREVIEW.accentOnLight};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${BLOG_PREVIEW.accentOnLight};
  }
`;

export const AtAGlanceBody = styled.p`
  margin: 0;
  color: ${BLOG_PREVIEW.lightText};
  font-size: 1.05rem;
  line-height: 1.65;
`;

/**
 * The rendered markdown body. Class selectors target the renderer's wl-contract
 * output inside `dangerouslySetInnerHTML` (see markdownToHtml.ts).
 */
export const PostBody = styled.div`
  color: ${BLOG_PREVIEW.lightTextSecondary};
  font-size: 1.05rem;
  line-height: 1.7;

  p {
    margin: 0 0 16px;
  }

  h2,
  h3 {
    color: ${BLOG_PREVIEW.lightText};
    font-weight: 800;
    line-height: 1.2;
    margin: 34px 0 12px;
    scroll-margin-top: 24px;
  }

  h2 {
    font-size: clamp(1.4rem, 2.6vw, 1.8rem);
  }

  h3 {
    font-size: clamp(1.15rem, 2vw, 1.35rem);
  }

  a {
    color: ${BLOG_PREVIEW.accentOnLight};
    text-decoration: underline;
  }

  strong {
    color: ${BLOG_PREVIEW.lightText};
  }

  ul.post-list,
  ol.post-list {
    margin: 0 0 16px;
    padding-left: 22px;
  }

  ul.post-list li::marker,
  ol.post-list li::marker {
    color: ${BLOG_PREVIEW.accentOnLight};
  }

  figure.post-figure {
    margin: 24px 0;

    img {
      display: block;
      width: 100%;
      border-radius: 14px;
      box-shadow: ${BLOG_PREVIEW.lightShadow};
    }

    figcaption {
      margin-top: 8px;
      text-align: center;
      font-size: 0.85rem;
      color: ${BLOG_PREVIEW.lightTextMuted};
    }
  }

  .data-table-wrap {
    margin: 24px 0;
    overflow-x: auto;
    border-radius: 12px;
    background: ${BLOG_PREVIEW.lightSurface};
    box-shadow: ${BLOG_PREVIEW.lightShadow};
  }

  table.data-table {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;

    th {
      text-align: left;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${BLOG_PREVIEW.lightTextMuted};
      background: ${BLOG_PREVIEW.lightBg};
      padding: 10px 14px;
    }

    td {
      padding: 10px 14px;
      border-top: 1px solid ${BLOG_PREVIEW.lightBorder};
    }

    tbody tr:nth-child(even) {
      background: ${BLOG_PREVIEW.tableZebra};
    }
  }

  aside.post-callout {
    display: grid;
    gap: 6px;
    margin: 24px 0;
    padding: 18px 24px;
    border-radius: 12px;
    background: ${BLOG_PREVIEW.lightSurface};
    box-shadow: ${BLOG_PREVIEW.lightShadow};

    .post-callout__label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: ${BLOG_PREVIEW.lightText};

      &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${BLOG_PREVIEW.accentOnLight};
      }
    }

    .post-callout__body > *:last-child {
      margin-bottom: 0;
    }
  }

  aside.post-callout--tip .post-callout__label::before {
    background: ${BLOG_PREVIEW.calloutTip};
  }

  aside.post-callout--warning .post-callout__label::before {
    background: ${BLOG_PREVIEW.calloutWarning};
  }

  details.post-toggle {
    margin: 24px 0;
    border-radius: 12px;
    background: ${BLOG_PREVIEW.lightSurface};
    box-shadow: ${BLOG_PREVIEW.lightShadow};
    overflow: hidden;

    summary.post-toggle__summary {
      cursor: pointer;
      padding: 14px 20px;
      font-weight: 650;
      list-style: none;
      display: flex;
      align-items: center;
      gap: 10px;

      &::-webkit-details-marker {
        display: none;
      }

      &::before {
        content: '';
        flex: 0 0 auto;
        width: 7px;
        height: 7px;
        border-right: 2px solid currentColor;
        border-bottom: 2px solid currentColor;
        transform: rotate(-45deg);
      }
    }

    &[open] summary.post-toggle__summary::before {
      transform: rotate(45deg);
    }

    .post-toggle__body {
      padding: 0 20px 14px 37px;

      > *:last-child {
        margin-bottom: 0;
      }
    }
  }

  /* The quiz is radio inputs read by :checked and :has() — no script here
     either, so the preview behaves exactly as the published page does. */
  div.post-quiz {
    margin: 26px 0;
    padding: 20px 22px;
    border-radius: 12px;
    border-left: 4px solid ${BLOG_PREVIEW.accentOnLight};
    background: ${BLOG_PREVIEW.lightSurface};
    box-shadow: ${BLOG_PREVIEW.lightShadow};

    .post-quiz__question {
      margin: 0 0 14px;
      font-weight: 650;
    }

    ul.post-quiz__options {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 8px;
    }

    li.post-quiz__option {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin: 0;
    }

    .post-quiz__input {
      margin: 3px 0 0;
      flex: 0 0 auto;
      accent-color: ${BLOG_PREVIEW.accentOnLight};
    }

    .post-quiz__label {
      cursor: pointer;
      flex: 1 1 auto;
    }

    /* The marks carry no text by design (see the renderer): they would have to
       be localized, and the author's explanation already carries the meaning. */
    .post-quiz__mark {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      opacity: 0;
    }

    .post-quiz__input:checked ~ .post-quiz__mark {
      opacity: 1;
    }

    li.post-quiz__option--right .post-quiz__mark {
      background: ${BLOG_PREVIEW.calloutTip};
      clip-path: polygon(14% 47%, 0 61%, 40% 100%, 100% 22%, 86% 8%, 39% 71%);
    }

    li.post-quiz__option--wrong .post-quiz__mark {
      background: ${BLOG_PREVIEW.calloutWarning};
      clip-path: polygon(
        20% 0,
        0 20%,
        30% 50%,
        0 80%,
        20% 100%,
        50% 70%,
        80% 100%,
        100% 80%,
        70% 50%,
        100% 20%,
        80% 0,
        50% 30%
      );
    }

    .post-quiz__answer {
      margin: 14px 0 0;
      padding-top: 14px;
      border-top: 1px solid ${BLOG_PREVIEW.tableZebra};
      font-size: 15px;
    }

    @supports selector(:has(*)) {
      .post-quiz__answer {
        display: none;
      }

      &:has(.post-quiz__input:checked) .post-quiz__answer {
        display: block;
      }
    }
  }

  figure.post-infographic {
    margin: 28px 0;

    svg {
      display: block;
      width: 100%;
      height: auto;
      border-radius: 12px;
    }

    figcaption {
      margin-top: 10px;
      font-size: 13px;
      text-align: center;
      color: ${BLOG_PREVIEW.lightText};
    }
  }

  blockquote.post-quote {
    margin: 24px 0;
    padding: 0 0 0 20px;
    border-left: 3px solid ${BLOG_PREVIEW.accentOnLight};
    color: ${BLOG_PREVIEW.lightText};
    font-size: 1.15rem;
    font-weight: 500;
  }

  pre.post-code {
    margin: 24px 0;
    padding: 16px 18px;
    border-radius: 12px;
    background: ${BLOG_PREVIEW.lightSurface};
    box-shadow: ${BLOG_PREVIEW.lightShadow};
    overflow-x: auto;
    font-size: 0.9rem;
  }

  code {
    background: ${BLOG_PREVIEW.codeBg};
    border-radius: 6px;
    padding: 1px 6px;
    font-size: 0.9em;
  }

  pre.post-code code {
    background: none;
    padding: 0;
  }

  hr {
    border: none;
    border-top: 1px solid ${BLOG_PREVIEW.lightBorder};
    margin: 32px 0;
  }
`;

/* Key takeaways checklist. */
export const KeyTakeaways = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 24px;
  border-radius: 14px;
  background: ${BLOG_PREVIEW.lightSurface};
  box-shadow: ${BLOG_PREVIEW.lightShadow};
`;

export const TakeawayList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TakeawayItem = styled.li`
  position: relative;
  padding-left: 28px;
  color: ${BLOG_PREVIEW.lightText};
  font-size: 1rem;
  line-height: 1.5;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${BLOG_PREVIEW.accentSoftOnLight};
  }

  &::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 6px;
    width: 8px;
    height: 5px;
    border-left: 2px solid ${BLOG_PREVIEW.accentOnLight};
    border-bottom: 2px solid ${BLOG_PREVIEW.accentOnLight};
    transform: rotate(-45deg);
  }
`;

/* Sources. */
export const Sources = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SourceList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${BLOG_PREVIEW.lightTextSecondary};
  font-size: 0.95rem;
  line-height: 1.7;

  a {
    color: ${BLOG_PREVIEW.accentOnLight};
  }
`;

export const LightHeading = styled(Text)`
  color: ${BLOG_PREVIEW.lightText};
  font-size: 1.3rem;
  font-weight: 800;
  margin: 0;
`;

/* ── Sticky aside (ToC) ── */

export const Aside = styled.aside`
  ${MOBILE} {
    order: -1;
  }
`;

export const AsideInner = styled.div`
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Toc = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
  border-radius: 14px;
  background: ${BLOG_PREVIEW.lightSurface};
  box-shadow: ${BLOG_PREVIEW.lightShadow};
`;

export const TocLabel = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${BLOG_PREVIEW.lightTextMuted};
`;

export const TocList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TocLink = styled.a`
  display: block;
  padding-left: 10px;
  border-left: 2px solid transparent;
  color: ${BLOG_PREVIEW.lightTextSecondary};
  font-size: 0.9rem;
  line-height: 1.4;
  text-decoration: none;

  &:hover {
    color: ${BLOG_PREVIEW.accentOnLight};
    border-left-color: ${BLOG_PREVIEW.accentOnLight};
  }
`;

/* ── Outro: FAQ + keep reading ── */

export const OutroPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 28px;
  border-radius: 16px;
  background: ${BLOG_PREVIEW.lightSurface};
  box-shadow: ${BLOG_PREVIEW.lightShadow};
`;

export const Eyebrow = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${BLOG_PREVIEW.accentOnLight};
`;

export const FaqItem = styled.details`
  border-top: 1px solid ${BLOG_PREVIEW.lightBorder};
  padding: 12px 0;

  &:first-of-type {
    border-top: none;
  }

  summary {
    cursor: pointer;
    color: ${BLOG_PREVIEW.lightText};
    font-weight: 600;
    font-size: 1rem;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }
  }

  p {
    margin: 10px 0 0;
    color: ${BLOG_PREVIEW.lightTextSecondary};
    line-height: 1.6;
  }
`;

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const PostCard = styled.button<{ $inert?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 18px;
  border-radius: 14px;
  border: 1px solid ${BLOG_PREVIEW.lightBorder};
  background: ${BLOG_PREVIEW.lightSurface};
  text-align: left;
  cursor: ${(p) => (p.$inert ? 'default' : 'pointer')};
  font-family: inherit;
  /* wl card hover lift; timing from the shared toast tokens (EXIT_MS ≈ 200ms). */
  transition: transform ${TOAST_ANIM.EXIT_MS}ms ease;

  &:hover {
    transform: ${(p) => (p.$inert ? 'none' : 'translateY(-3px)')};
  }
`;

export const CardLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${BLOG_PREVIEW.accentOnLight};
`;

export const CardTitle = styled.span`
  color: ${BLOG_PREVIEW.lightText};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
`;

export const CardDescription = styled.span`
  color: ${BLOG_PREVIEW.lightTextMuted};
  font-size: 0.85rem;
  line-height: 1.5;
`;

export const PageNav = styled.nav`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const PageNavLink = styled.button<{ $next?: boolean; $inert?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: ${(p) => (p.$next ? 'flex-end' : 'flex-start')};
  text-align: ${(p) => (p.$next ? 'right' : 'left')};
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid ${BLOG_PREVIEW.lightBorder};
  background: ${BLOG_PREVIEW.lightSurface};
  cursor: ${(p) => (p.$inert ? 'default' : 'pointer')};
  font-family: inherit;

  &:hover {
    border-color: ${(p) => (p.$inert ? BLOG_PREVIEW.lightBorder : BLOG_PREVIEW.accentOnLight)};
  }
`;

export const PageNavDir = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${BLOG_PREVIEW.lightTextMuted};
`;

export const PageNavTitle = styled.span`
  color: ${BLOG_PREVIEW.lightText};
  font-size: 0.95rem;
  font-weight: 600;
`;

/* ── the byline ─────────────────────────────────────────────────────────── */

/* The public page shows the author twice: the name in the cover byline, linking
   down, and the full card near the bottom. The preview mirrors both, because it
   is the promise about what the published page will look like. */
export const BylineAuthorLink = styled.a`
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

export const AuthorCard = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;

  ${MOBILE} {
    flex-direction: column;
    gap: 12px;
  }
`;

export const AuthorAvatar = styled.img`
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
`;

export const AuthorName = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
`;

export const AuthorRole = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  color: ${BLOG_PREVIEW.lightText};
`;

export const AuthorBio = styled.p`
  margin: 10px 0 0;
`;

export const AuthorLinks = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 12px 0 0;
  padding: 0;

  a {
    font-size: 13px;
    font-weight: 600;
    color: ${BLOG_PREVIEW.accentOnLight};
  }
`;
